// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { Pencil, Store } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useChainDetailQuery } from "@/api/hooks/use-chains";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChainActionsMenu } from "@/features/chains/chain-actions-menu";
import { CompareChainDialog } from "@/features/chains/compare-chains-dialog";
import { CreateChainDialog } from "@/features/chains/create-chain-dialog";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

export const Route = createFileRoute("/_authenticated/chains/$chainName")({
  validateSearch: z.object({ t: z.string().optional(), f: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.chains}>
      <ChainDetailLayout />
    </RequirePermission>
  ),
});

/**
 * Chrome shared by the chain's read page (`/chains/$chainName`) and its edit page
 * (`/chains/$chainName/edit`): the identity pill row, the actions menu and its dialogs.
 * Keeping it in the layout means the header stays mounted when moving between the two.
 */
function ChainDetailLayout() {
  const { chainName } = Route.useParams();
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const matchRoute = useMatchRoute();
  const { data: chain, isLoading, isError } = useChainDetailQuery(chainName);
  const { data: ctx } = useBackofficeContext();

  // The button that opens the edit page is hidden once we're there — the chain-name breadcrumb
  // is the way back.
  const isEditPage = !!matchRoute({ to: "/chains/$chainName/edit" });
  const canEdit = hasPermission(PAGE_PERMISSIONS.chainsEdit);

  const displayName = chain?.legibleName ?? ctx?.chains?.find((c) => c.value === chainName)?.display ?? chainName;

  const breadcrumbs = useMemo(
    () =>
      isEditPage
        ? [
            { label: t("chains.title"), to: "/chains" },
            { label: displayName, to: `/chains/${chainName}`, bold: true },
          ]
        : [{ label: t("chains.title"), to: "/chains" }],
    [t, isEditPage, displayName, chainName],
  );

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <PageHeader title={chainName} breadcrumbs={[{ label: t("chains.title"), to: "/chains" }]} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isEditPage ? t("buttons.edit") : displayName}
        subtitle={
          <>
            {chain && <IdPill id={chain.chainId} />}
            <KeyChip label={chainName} variant="chain" />
            {chain && <StatusIndicator active={chain.isActive} />}
            {chain && (
              <Badge variant="secondary" className="gap-1.5 font-normal text-xs [&>svg]:size-2.5">
                <Store />
                {t("chains.storeCount", { count: chain.storesCount })}
              </Badge>
            )}
            <div className="ml-auto -my-1.5 flex items-center gap-2">
              {/* 30px rather than the `sm` 32px — sits a touch tighter in the pill row */}
              {canEdit && !isEditPage && (
                <Button asChild size="sm" className="h-7.5">
                  <Link to="/chains/$chainName/edit" params={{ chainName }} search={{}}>
                    <Pencil className="h-4 w-4" />
                    {t("buttons.edit")}
                  </Link>
                </Button>
              )}
              <ChainActionsMenu
                onDuplicate={() => setCreateDialogOpen(true)}
                onCompareSettings={() => setCompareDialogOpen(true)}
              />
            </div>
          </>
        }
        breadcrumbs={breadcrumbs}
      />

      <Outlet />

      <CreateChainDialog
        key={`duplicate-${chainName}`}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        duplicateFrom={{
          copyFrom: chainName,
          defaultTimeZone: chain?.defaultTimeZone ?? "",
        }}
      />

      <CompareChainDialog defaultChainName={chainName} open={compareDialogOpen} onOpenChange={setCompareDialogOpen} />
    </div>
  );
}
